import { useCallback, useContext } from "react";
import { CheckoutRoute } from "@swiggy-private/checkout-types";

import { CheckoutContext } from "@checkout-core/contexts/CheckoutContext";
import { useNavigate } from "@checkout-core/hooks/UseNavigate";
import { CheckoutActionTypes } from "@checkout-core/reducers/Checkout";

import { QUICK_CHECKOUT_NAV_DIRECTION, recordQuickCheckoutNav } from "@checkout-core/helpers/QuickCheckoutNREvents";
import { isQuickCheckoutFlow, setQuickCheckoutBackgroundTransparent } from "@checkout-core/helpers/QuickCheckout";
import { PlaceOrderData } from "@checkout-core/interfaces/Checkout";

import {
    applyQuickCheckoutEnterTransition,
    beginInboundQcHeightTransition,
    changeWebviewHeight,
    endInboundQcHeightTransition,
    isInboundQcHeightPending,
    refreshQuickCheckoutParentCollection,
    resetScreenTransition,
} from "./helpers";
import {
    HEIGHT_NAVIGATION_PAYLOAD,
    QC_FOOTER_TYPE,
    QC_FOOTER_TYPE_PAYLOAD,
} from "@checkout-core/constants/QuickCheckout";

export const useQuickCheckoutNavHandlers = () => {
    const { setCheckoutState } = useContext(CheckoutContext);
    const navigateTo = useNavigate();

    const applyQuickCheckoutWebviewScale = useCallback((scale: QC_FOOTER_TYPE, callback?: VoidFunction) => {
        if (isInboundQcHeightPending()) {
            return;
        }
        changeWebviewHeight(QC_FOOTER_TYPE_PAYLOAD[scale], callback);
    }, []);

    /**
     * QC turbo place-order failure: returns a post-native-alert callback that unscales the
     * webview back to the compact sheet. No-ops (undefined) outside QC / non-turbo so BAU
     * place-order alerts are untouched.
     */
    const handleResetOnPlaceOrderFailure = useCallback(
        (placeOrderData: PlaceOrderData | undefined, activeRouteName: string): VoidFunction | undefined => {
            if (!isQuickCheckoutFlow()) {
                return undefined;
            }
            if (
                placeOrderData?.extraArbitaryData?.isTurboPaymentMethod !== true ||
                activeRouteName !== CheckoutRoute.QUICK_CHECKOUT
            ) {
                return undefined;
            }
            return () => applyQuickCheckoutWebviewScale(QC_FOOTER_TYPE.DEFAULT);
        },
        [applyQuickCheckoutWebviewScale],
    );
    const beginInboundQcTransition = useCallback(() => {
        beginInboundQcHeightTransition();
        applyQuickCheckoutEnterTransition();
    }, []);

    const settleInboundQcTransition = useCallback(() => {
        resetScreenTransition();
        endInboundQcHeightTransition();
    }, []);

    const abortInboundQcTransition = useCallback(() => {
        settleInboundQcTransition();
        setQuickCheckoutBackgroundTransparent(false);
    }, [settleInboundQcTransition]);

    // Outbound QC → Payments: scale up then navigate. Inbound height lock is only for
    // Payments/MRO → QC (see beginInboundQcHeightTransition on those handlers).
    const handleQuickCheckoutToPayments = useCallback(() => {
        recordQuickCheckoutNav({ direction: QUICK_CHECKOUT_NAV_DIRECTION.QC_TO_PAYMENTS });
        resetScreenTransition();
        changeWebviewHeight(HEIGHT_NAVIGATION_PAYLOAD.QC_TO_PAYMENTS, () => {
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_PAYMENTS,
                payload: {},
            });
            navigateTo(CheckoutRoute.PAYMENTS, {}, false, {
                onTransitionComplete: () => {
                    setQuickCheckoutBackgroundTransparent(false);
                },
            });
        });
    }, [setCheckoutState, navigateTo]);

    // This is called when user is coming from payments to quick checkout
    // here we need to navigate first and then scale down the webview
    // navigating first goes from 100% webview to 100% QC webview with 200dp cart
    // later the 100% webview scales down to 200dp cart
    const handlePaymentsToQuickCheckout = useCallback(
        (scale: QC_FOOTER_TYPE = QC_FOOTER_TYPE.DEFAULT) => {
            recordQuickCheckoutNav({ direction: QUICK_CHECKOUT_NAV_DIRECTION.PAYMENTS_TO_QC });
            beginInboundQcTransition();
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_QUICK_CHECKOUT,
                payload: { refreshCartDataForQuickCheckout: true, refetchGPOnQCRemount: true },
            });
            navigateTo(CheckoutRoute.QUICK_CHECKOUT, {}, true, {
                // QC is out-in: exit payments first, then enter QC. Flip transparent on exit
                // so the sheet slides up over native UI without punching through payments.
                onTransitionExit: () => {
                    endInboundQcHeightTransition();
                    setQuickCheckoutBackgroundTransparent(true);
                    applyQuickCheckoutWebviewScale(scale, () => {
                        refreshQuickCheckoutParentCollection();
                    });
                },
                onTransitionComplete: () => {
                    settleInboundQcTransition();
                },
                errorCallback: abortInboundQcTransition,
            });
            // Clear once consumed so later payment back-nav does not incorrectly re-enter QC.
            sessionStorage.removeItem("isFromQuickCheckout");
        },
        [
            setCheckoutState,
            navigateTo,
            applyQuickCheckoutWebviewScale,
            beginInboundQcTransition,
            settleInboundQcTransition,
            abortInboundQcTransition,
        ],
    );

    // This is called when user is coming from quick checkout to MRO food cart
    // here we need to scale up the webview first and then navigate
    // scaling up the webview goes from 200dp cart to 100% webview
    // later the 100% webview navigates to MRO food cart
    const handleQuickCheckoutToMroFoodCart = useCallback(
        (payload?: { openedAddressFromQuickCheckout?: boolean }) => {
            // NOTE: MRO and Quick Checkout Share the same cart context so no refetch on cart is needed
            recordQuickCheckoutNav({ direction: QUICK_CHECKOUT_NAV_DIRECTION.QC_TO_MRO });
            resetScreenTransition();
            changeWebviewHeight(HEIGHT_NAVIGATION_PAYLOAD.QC_TO_MRO_FOOD_CART, () => {
                setCheckoutState({
                    type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
                    payload,
                });
                navigateTo(CheckoutRoute.MRO_FOOD_CART, undefined, undefined, {
                    onTransitionComplete: () => {
                        setQuickCheckoutBackgroundTransparent(false);
                    },
                });
            });
        },
        [setCheckoutState, navigateTo],
    );

    // This is called when user is coming from MRO food cart to quick checkout
    // here we need to navigate first and then scale down the webview
    // navigating first goes from 100% webview to 100% QC webview with 200dp cart
    // later the 100% webview scales down to 200dp cart
    const handleMroFoodCartToQuickCheckout = useCallback(
        (scale: QC_FOOTER_TYPE = QC_FOOTER_TYPE.DEFAULT, refreshParentScreenOnRemount = false) => {
            recordQuickCheckoutNav({ direction: QUICK_CHECKOUT_NAV_DIRECTION.MRO_TO_QC });
            beginInboundQcTransition();
            setCheckoutState({
                type: CheckoutActionTypes.SHOW_QUICK_CHECKOUT,
                payload: { refetchGPOnQCRemount: true },
            });
            setQuickCheckoutBackgroundTransparent(true);
            navigateTo(CheckoutRoute.QUICK_CHECKOUT, {}, true, {
                // Same as payments→QC (out-in): transparent after MRO exits, before QC enters.
                onTransitionExit: () => {
                    endInboundQcHeightTransition();
                    applyQuickCheckoutWebviewScale(scale, () => {
                        if (refreshParentScreenOnRemount) {
                            refreshQuickCheckoutParentCollection();
                        }
                    });
                },
                onTransitionComplete: () => {
                    settleInboundQcTransition();
                },
                errorCallback: abortInboundQcTransition,
            });
        },
        [
            setCheckoutState,
            navigateTo,
            applyQuickCheckoutWebviewScale,
            beginInboundQcTransition,
            settleInboundQcTransition,
            abortInboundQcTransition,
        ],
    );

    // This is called when user is coming from quick checkout to turbo place order
    // here we need to scale up the webview first and then navigate
    // scaling up the webview goes from 200dp cart to 100% webview
    // later the 100% webview navigates to turbo place order
    const handleQuickCheckoutToTurboPlaceOrder = useCallback((callback?: VoidFunction) => {
        recordQuickCheckoutNav({ direction: QUICK_CHECKOUT_NAV_DIRECTION.QC_TO_TURBO_PO });
        // Same as other outbound hops: clear leftover enter-transition CSS vars before scaling up.
        resetScreenTransition();
        changeWebviewHeight(HEIGHT_NAVIGATION_PAYLOAD.QC_TO_PAYMENTS, () => {
            // Class toggle is deferred to UsePlaceOrder for QC turbo place order
            // (status 0 clears html.quick-checkout; failure restores it). Height change only here.
            if (typeof callback === "function") {
                callback();
            }
        });
    }, []);

    return {
        handleQuickCheckoutToPayments,
        handlePaymentsToQuickCheckout,
        handleQuickCheckoutToMroFoodCart,
        handleMroFoodCartToQuickCheckout,
        handleQuickCheckoutToTurboPlaceOrder,
        handleResetOnPlaceOrderFailure,
        applyQuickCheckoutWebviewScale,
    };
};
